/**
 * Chat store — wires streamChat + sseReducer.
 * RESEARCH §Code Examples useChat.ts adaptado a Zustand 5.
 *
 * T-04-01-05 mitigation: cancel() llama abortController.abort() — backend
 * detecta is_disconnected() y emite event:cancelled.
 */
import type { GuiaCliente as GuiaClienteT } from '../types/sse'
import { create } from 'zustand'
import { streamChat } from '../api/chat'
import { sseReducer, type SseState } from './sseReducer'
import type { ProfileType } from '../types/profile'
import type { ProductCard } from '../types/product_card'
import type { Derivacion } from '../types/sse'
import type { AccionRespuesta } from '../api/accion'

export type ChatRole = 'me' | 'staff' | 'system'

export interface Message {
  role: ChatRole
  text?: string
  card?: ProductCard
  /** PR B 2026-05-08 — suggestions chips para click-to-send. Aparecen cuando
   *  el agente pide aclaración (ambigüedad) o ofrece alternativas conversacionales. */
  suggestions?: string[]
  /** 2026-10-01 — pregunta de triaje (sin producto todavía): no lleva cierre. */
  triaje?: boolean
  /** 2026-10-01 — qué decirle al cliente y cuándo derivar (guía clínica) */
  guia?: GuiaClienteT | null
  /** 2026-09-30 — derivación por señal de alarma; la UI la muestra arriba de todo. */
  derivacion?: Derivacion[]
  /** Plan 04-03: trace_id propagado desde event:complete del backend para que
   *  FeedbackWidget pueda correlacionar feedback ↔ Langfuse trace. Null si
   *  Langfuse off (fail-open). */
  trace_id?: string | null
  /** 2026-10-01 — id de la consulta en el registro (feedback sin Langfuse). */
  consulta_id?: string | null
  ts: number
}

interface SendOpts {
  profile?: ProfileType | null
  device_id: string
  device_token: string
  session_id?: string
}

interface ChatStore {
  sseState: SseState
  messages: Message[]
  abortController: AbortController | null
  send: (text: string, opts: SendOpts) => Promise<void>
  /** 2026-10-01 — acción instantánea del caso (más barato, otra forma, dosis por peso). */
  accionar: (pedido: string, run: () => Promise<AccionRespuesta>) => Promise<void>
  /** "Deshacer" de Nuevo cliente: vuelve a poner la conversación anterior. */
  restaurar: (messages: Message[]) => void
  cancel: () => void
  reset: () => void
}

export const useChatStore = create<ChatStore>((set, get) => ({
  sseState: { status: 'idle' },
  messages: [],
  abortController: null,

  send: async (text, opts) => {
    const ctrl = new AbortController()
    set((s) => ({
      abortController: ctrl,
      messages: [...s.messages, { role: 'me', text, ts: Date.now() }],
      sseState: sseReducer(s.sseState, { type: 'send' }),
    }))

    try {
      await streamChat({
        message: text,
        device_id: opts.device_id,
        device_token: opts.device_token,
        session_id: opts.session_id,
        profile: opts.profile,
        signal: ctrl.signal,
        onProgress: (evt) => {
          set((s) => ({ sseState: sseReducer(s.sseState, { type: 'progress', evt }) }))
        },
        onTerminal: (evt) => {
          set((s) => {
            const next = sseReducer(s.sseState, { type: 'terminal', evt })
            const messages =
              evt.type === 'complete'
                ? [
                    ...s.messages,
                    {
                      role: 'staff' as const,
                      // null cuando ambigüedad/red flag/jailbreak — el
                      // ChatStream cae a la branche text-only.
                      card: evt.card ?? undefined,
                      // PR B 2026-05-08 — texto natural del AgentRunner
                      // (saludo, pregunta clarificadora, derivación a médico).
                      // Cuando card existe, se renderiza arriba como burbuja
                      // staff explicativa.
                      text: evt.agent_message?.text ?? undefined,
                      suggestions: evt.agent_message?.suggestions ?? undefined,
                      triaje: evt.agent_message?.triaje ?? undefined,
                      guia: evt.agent_message?.guia ?? undefined,
                      derivacion: evt.derivacion ?? evt.agent_message?.derivacion ?? undefined,
                      // Plan 04-03 — trace_id del backend (puede ser null si
                      // Langfuse off, fail-open). FeedbackWidget detecta null
                      // y oculta para evitar feedback orphan.
                      trace_id: evt.trace_id ?? null,
                      consulta_id: evt.consulta_id ?? null,
                      ts: Date.now(),
                    },
                  ]
                : s.messages
            return { sseState: next, abortController: null, messages }
          })
        },
      })
    } catch (e) {
      // T-04-01-07: NO incluir el error completo (puede tener body remoto).
      // El sseReducer formatea mensaje user-facing genérico.
      set((s) => ({
        sseState: sseReducer(s.sseState, {
          type: 'terminal',
          evt: { type: 'error', errors: [String(e).slice(0, 80)] },
        }),
        abortController: null,
      }))
    }
  },

  accionar: async (pedido, run) => {
    set((s) => ({
      messages: [...s.messages, { role: 'me', text: pedido, ts: Date.now() }],
      sseState: { status: 'thinking' },
    }))
    try {
      const r = await run()
      set((s) => ({
        sseState: { status: 'complete', card: r.card ?? null, trace_id: null },
        messages: [
          ...s.messages,
          {
            role: 'staff' as const,
            card: r.card ?? undefined,
            text: r.agent_message?.text ?? undefined,
            suggestions: r.agent_message?.suggestions ?? undefined,
            trace_id: null,
            consulta_id: r.consulta_id ?? null,
            ts: Date.now(),
          },
        ],
      }))
    } catch (e) {
      set(() => ({ sseState: { status: 'error', message: String((e as Error).message ?? e).slice(0, 160), retry: false } }))
    }
  },

  restaurar: (messages) => set({ messages, sseState: { status: 'idle' }, abortController: null }),

  cancel: () => {
    get().abortController?.abort()
    set({ sseState: { status: 'idle' }, abortController: null })
  },

  reset: () => {
    get().abortController?.abort()
    set({ sseState: { status: 'idle' }, messages: [], abortController: null })
  },
}))
